import { createTheme } from '@mui/material/styles';

// Much darker gradient color palette - more subtle and dark
const darkGradientPalette = {
  // Main gradient colors - much darker versions
  gradientTop: '#6B46C1',     // Darker purple
  gradientMid: '#4C1D95',     // Much deeper purple  
  gradientBottom: '#1E1B4B',  // Dark navy (kept)
  gradientDeep: '#0A0A0F',    // Almost black
  
  // Much more subtle glass morphism colors with purple tints
  glassLight: 'rgba(88, 56, 154, 0.18)',
  glassMid: 'rgba(76, 29, 149, 0.15)',
  glassDark: 'rgba(30, 27, 75, 0.12)',
  glassBorder: 'rgba(107, 70, 193, 0.18)',
  
  // More subdued accent colors
  accent1: '#7C3AED',
  accent2: '#8B5CF6',
  accent3: '#A78BFA',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: darkGradientPalette.gradientTop,
      light: darkGradientPalette.accent2,
      dark: darkGradientPalette.gradientMid,
    },
    secondary: {
      main: darkGradientPalette.accent1,
      light: darkGradientPalette.accent2,
      dark: darkGradientPalette.gradientMid,
    },
    background: {
      default: darkGradientPalette.gradientDeep,
      paper: darkGradientPalette.gradientBottom,
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.9)',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
    divider: darkGradientPalette.glassBorder,
    action: {
      hover: 'rgba(107, 70, 193, 0.08)',
    },
  },
  typography: {
    fontFamily: '"Satoshi", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          background: darkGradientPalette.gradientDeep,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          scrollbarColor: `${darkGradientPalette.gradientTop} ${darkGradientPalette.gradientDeep}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '12px',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: darkGradientPalette.gradientDeep,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            background: darkGradientPalette.gradientTop,
            borderRadius: '6px',
            border: `2px solid ${darkGradientPalette.gradientDeep}`,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            background: darkGradientPalette.accent2,
          },
        },
        // Global glass morphism CSS variables - restored for visibility
        ':root': {
          '--glass-bg-light': darkGradientPalette.glassLight,
          '--glass-bg-mid': darkGradientPalette.glassMid,
          '--glass-bg-dark': darkGradientPalette.glassDark,
          '--glass-border': darkGradientPalette.glassBorder,
          '--glass-border-hover': 'rgba(107, 70, 193, 0.15)',
          '--gradient-top': darkGradientPalette.gradientTop,
          '--gradient-mid': darkGradientPalette.gradientMid,
          '--gradient-bottom': darkGradientPalette.gradientBottom,
          '--gradient-deep': darkGradientPalette.gradientDeep,
          '--accent-1': darkGradientPalette.accent1,
          '--accent-2': darkGradientPalette.accent2,
          '--accent-3': darkGradientPalette.accent3,
        },
        // Global CSS overrides to fix all border styling issues
        '*': {
          // Override any element that has explicit borderColor set to use our glass border
          '&[style*="border-color: rgb(255, 255, 255)"], &[style*="border-color: rgb(0, 0, 0)"], &[style*="border-color: white"], &[style*="border-color: black"]': {
            borderColor: `${darkGradientPalette.glassBorder} !important`,
          },
        },
        // Fix specific border patterns commonly used in MUI components
        '.MuiBox-root, .MuiPaper-root, .MuiCard-root, .MuiDialog-paper': {
          '&[style*="border-bottom: 1px solid"]': {
            borderBottomColor: `${darkGradientPalette.glassBorder} !important`,
          },
          '&[style*="border-top: 1px solid"]': {
            borderTopColor: `${darkGradientPalette.glassBorder} !important`,
          },
          '&[style*="border-left: 1px solid"]': {
            borderLeftColor: `${darkGradientPalette.glassBorder} !important`,
          },
          '&[style*="border-right: 1px solid"]': {
            borderRightColor: `${darkGradientPalette.glassBorder} !important`,
          },
        },
        // Target specific CSS classes that might have border issues
        '[class*="borderColor"]': {
          borderColor: `${darkGradientPalette.glassBorder} !important`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          backdropFilter: 'blur(40px)',
          border: `1px solid ${darkGradientPalette.glassBorder}`,
                  '&:hover': {
          backdropFilter: 'blur(45px)',
          border: `1px solid rgba(107, 70, 193, 0.18)`,
        },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: darkGradientPalette.glassMid,
          backdropFilter: 'blur(40px)',
          border: `1px solid ${darkGradientPalette.glassBorder}`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(107, 70, 193, 0.12), inset 0 -1px 0 rgba(107, 70, 193, 0.02)',
          '&.MuiDialog-paper': {
            background: darkGradientPalette.glassMid,
            backdropFilter: 'blur(40px)',
            border: `1px solid ${darkGradientPalette.glassBorder}`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.8)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: darkGradientPalette.glassDark,
            backdropFilter: 'blur(32px)',
            transition: 'all 0.3s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkGradientPalette.glassBorder,
            },
            '&:hover': {
              background: darkGradientPalette.glassMid,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(107, 70, 193, 0.18)',
              }
            },
            '&.Mui-focused': {
              background: darkGradientPalette.glassMid,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: darkGradientPalette.gradientTop,
                boxShadow: `0 0 20px ${darkGradientPalette.gradientTop}30`,
              }
            }
          }
        }
      }
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                elevation: 0,
                background: darkGradientPalette.glassDark,
                backdropFilter: 'blur(40px)',
                borderBottom: `1px solid ${darkGradientPalette.glassBorder}`,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
            }
        }
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                background: darkGradientPalette.glassMid,
                backdropFilter: 'blur(40px)',
                borderRight: `1px solid ${darkGradientPalette.glassBorder}`,
            }
        }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: darkGradientPalette.glassBorder,
          background: `linear-gradient(90deg, transparent 0%, ${darkGradientPalette.glassBorder} 50%, transparent 100%)`,
          height: '1px',
          opacity: 0.6,
          '&.MuiDivider-inset': {
            background: `linear-gradient(90deg, transparent 0%, ${darkGradientPalette.gradientTop}40 50%, transparent 100%)`,
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          background: 'transparent',
          '&.MuiGrid-container': {
            background: 'transparent',
          },
          '&.MuiGrid-item': {
            background: 'transparent',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          background: 'transparent',
          '&.MuiContainer-maxWidthXs, &.MuiContainer-maxWidthSm, &.MuiContainer-maxWidthMd, &.MuiContainer-maxWidthLg, &.MuiContainer-maxWidthXl': {
            background: 'transparent',
          },
        },
      },
    },
    // Handle Card components specifically for better border control
    MuiCard: {
      styleOverrides: {
        root: {
          background: darkGradientPalette.glassMid,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${darkGradientPalette.glassBorder}`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(107, 70, 193, 0.1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(107, 70, 193, 0.15)',
          },
        },
      },
    },
    // Add Dialog and Paper component overrides
    MuiDialog: {
      styleOverrides: {
        root: {
          '& .MuiDialog-paper': {
            background: darkGradientPalette.glassMid,
            backdropFilter: 'blur(40px)',
            border: `1px solid ${darkGradientPalette.glassBorder}`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.8)',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.8)',
          borderBottom: `1px solid ${darkGradientPalette.glassBorder}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          background: 'transparent',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          background: 'transparent',
          borderTop: `1px solid ${darkGradientPalette.glassBorder}`,
        },
      },
    },

    // Handle Tab components and related borders
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: darkGradientPalette.gradientTop,
          },
        },
      },
    },
    // Handle List components to fix borders in dialogs and other areas
    MuiList: {
      styleOverrides: {
        root: {
          background: 'transparent',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderColor: darkGradientPalette.glassBorder,
        },
      },
    },
  }
});

export default theme; 